// ============================================================================
// contacts/directory.ts — КАТАЛОГ КОНТАКТОВ (SSoT, чистые функции).
//
// Один контакт = одна сущность ПЛЮС её внешние идентичности. Если несколько
// сущностей ссылаются на ОДНУ внешнюю идентичность (adapter_id + external_id),
// они склеиваются в одну карточку: живой профиль человека важнее технической
// записи tg_*, каналы объединяются без дублей, выбор канонической записи —
// детерминированный (не зависит от порядка ответа сервера).
// ============================================================================

import { externalKey, mergeChannels } from './channels';
import type { ContactSummary } from './types';

export interface DirectoryEntry {
  summary: ContactSummary;
  links: Record<string, unknown>[];
}

/** Лучшая запись для карточки: человек важнее технического контакта. */
function preferContact(a: ContactSummary, b: ContactSummary): ContactSummary {
  if (a.technical !== b.technical) return a.technical ? b : a;
  if (a.channels.length !== b.channels.length) return a.channels.length >= b.channels.length ? a : b;
  const byName = a.name.localeCompare(b.name, 'ru');
  if (byName !== 0) return byName < 0 ? a : b;
  return a.id <= b.id ? a : b;
}

/** Объединение записей с общей внешней идентичностью (union-find по ключам). */
export function buildContactDirectory(entries: DirectoryEntry[]): ContactSummary[] {
  const parent = new Map<string, string>();
  const find = (key: string): string => {
    let root = key;
    while (parent.get(root) !== undefined && parent.get(root) !== root) root = parent.get(root) as string;
    let current = key;
    while (current !== root) {
      const next = parent.get(current) as string;
      parent.set(current, root);
      current = next;
    }
    return root;
  };
  const union = (a: string, b: string): void => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent.set(rb, ra);
  };

  const entryKeys = entries.map((entry) => {
    const keys = entry.links.map(externalKey).filter(Boolean);
    return Array.from(new Set(keys));
  });
  for (const keys of entryKeys) {
    if (keys.length === 0) continue;
    for (const key of keys) if (!parent.has(key)) parent.set(key, key);
    for (let index = 1; index < keys.length; index += 1) union(keys[0], keys[index]);
  }

  const groups = new Map<string, DirectoryEntry[]>();
  const order: string[] = [];
  entries.forEach((entry, index) => {
    const keys = entryKeys[index];
    const root = keys.length > 0 ? find(keys[0]) : `contact:${entry.summary.id}`;
    let group = groups.get(root);
    if (!group) {
      group = [];
      groups.set(root, group);
      order.push(root);
    }
    group.push(entry);
  });

  const contacts: ContactSummary[] = [];
  for (const root of order) {
    const members = groups.get(root) as DirectoryEntry[];
    let canonical = members[0].summary;
    for (const member of members) canonical = preferContact(canonical, member.summary);

    const merged: ContactSummary = {
      ...canonical,
      channels: canonical.channels.map((channel) => ({ ...channel })),
    };
    for (const member of members) {
      const summary = member.summary;
      if (summary.id !== canonical.id) mergeChannels(merged.channels, summary.channels);
      if (!merged.phone && summary.phone) merged.phone = summary.phone;
      if (!merged.email && summary.email) merged.email = summary.email;
      if (!merged.city && summary.city) merged.city = summary.city;
      if (!merged.avatarUrl && summary.avatarUrl) merged.avatarUrl = summary.avatarUrl;
    }
    // Контакт «технический» только если ВСЕ записи технические (есть человек —
    // карточка человеческая).
    merged.technical = members.every((member) => member.summary.technical);
    contacts.push(merged);
  }

  contacts.sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  return contacts;
}

/**
 * Поиск по карточке контакта: имя, логин, телефон, email, город и ВНЕШНИЕ
 * идентичности (значение/имя привязки — username Telegram/MAX). Телефон и
 * внешние номера сравниваются по цифрам (скобки/дефисы/@ не мешают).
 */
export function contactMatchesQuery(contact: ContactSummary, query: string): boolean {
  const raw = query.trim().toLowerCase();
  if (!raw) return true;
  const atless = raw.replace(/^@+/, '');
  const fields = [
    contact.name,
    contact.login,
    contact.phone,
    contact.email,
    contact.city,
    ...contact.channels.flatMap((channel) => [
      channel.value,
      channel.externalName || '',
      channel.adapterName || '',
      channel.label,
    ]),
  ]
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);
  if (fields.some((value) => value.includes(raw) || (atless.length > 0 && value.includes(atless)))) {
    return true;
  }
  const digits = raw.replace(/\D+/g, '');
  if (digits.length < 3) return false;
  const phoneDigits = contact.phone.replace(/\D+/g, '');
  if (phoneDigits.includes(digits)) return true;
  return contact.channels.some((channel) => {
    const channelDigits = channel.value.replace(/\D+/g, '');
    return channelDigits.includes(digits)
      || (channel.externalName || '').replace(/\D+/g, '').includes(digits);
  });
}
